<script setup lang="ts">
/** Four or more photos and videos sent together, shown as WhatsApp does: a 2×2 grid, "+N" on the fourth. */
import { computed } from "vue";
import Icon from "./Icon.vue";
import MessageStatus from "./MessageStatus.vue";
import { albumDelivery } from "../composables/messageDelivery";
import type { Message } from "../data/demo";
import { t } from "../i18n";

const props = withDefaults(
  defineProps<{ messages: Message[]; tail?: boolean }>(),
  { tail: true },
);
const emit = defineEmits<{
  media: [message: Message];
  menu: [e: MouseEvent, message: Message];
}>();

const tiles = computed(() => props.messages.slice(0, 4));
const extra = computed(() => Math.max(0, props.messages.length - 4));
const last = computed(() => props.messages[props.messages.length - 1]!);
const out = computed(() => props.messages[0]!.out);
const delivery = computed(() => albumDelivery(props.messages));
</script>

<template>
  <div class="message-line" :class="{ out, continued: !tail }">
    <div class="bubble-wrap">
      <article
        class="message-bubble album media-only"
        :aria-label="t('media.album', { count: messages.length })"
      >
        <div class="album-grid">
          <button
            v-for="(m, i) in tiles"
            :key="m.id"
            type="button"
            class="album-tile"
            :aria-label="
              i === 3 && extra
                ? t('media.album_more', { count: extra })
                : t('media.open')
            "
            @click="emit('media', m)"
            @contextmenu.prevent.stop="emit('menu', $event, m)"
          >
            <img v-if="m.kind === 'image'" :src="m.src" alt="" loading="lazy" />
            <video
              v-else
              :src="m.src ? m.src + '#t=0.1' : undefined"
              preload="metadata"
              muted
              playsinline
            />
            <span
              v-if="m.kind === 'video' && !(i === 3 && extra)"
              class="video-play small"
              ><Icon name="play" :size="22"
            /></span>
            <span
              v-if="m.uploading !== undefined"
              class="upload-ring"
              :style="{ '--progress': Math.round(m.uploading * 100) + '%' }"
            />
            <span v-if="i === 3 && extra" class="album-more">+{{ extra }}</span>
          </button>
        </div>
        <div class="message-meta">
          <time>{{ last.time }}</time
          ><MessageStatus v-if="out" :status="delivery" />
        </div>
      </article>
    </div>
  </div>
</template>
